import { useMemo, useState } from 'react';
import { type CollectionAdminItem, LOCALES, type Locale } from '@bailafm/shared';
import { Check, ChevronsUpDown } from 'lucide-react';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';

// Locales que le faltan al label de una colección. Una colección sólo
// puede asignarse a la home si los tiene los tres (invariante que la
// API valida en el PUT; aquí sólo lo reflejamos en la UI).
export function missingLocales(c: CollectionAdminItem): Locale[] {
  return LOCALES.filter((l) => typeof c.label[l] !== 'string' || c.label[l]!.length === 0);
}

// Texto con el que se identifica una colección en el admin. Cascada
// es → ca → en para que una colección incompleta siga siendo
// reconocible en la lista.
export function previewFromLabel(c: CollectionAdminItem): string {
  return c.label.es ?? c.label.ca ?? c.label.en ?? c.id;
}

interface CollectionComboboxProps {
  value: string;
  collections: CollectionAdminItem[];
  onChange(collectionId: string): void;
}

/**
 * Selector de colección con búsqueda. Sustituye al `<select>` nativo,
 * que era inmanejable en cuanto el listado de colecciones crece.
 *
 * La búsqueda cubre los títulos en los tres idiomas y el slug, así que
 * encuentra la colección aunque quien la busque la conozca por el
 * nombre en otro idioma. Las colecciones sin los tres idiomas salen
 * deshabilitadas, igual que en el select anterior.
 */
export function CollectionCombobox({
  value,
  collections,
  onChange,
}: CollectionComboboxProps) {
  const [open, setOpen] = useState(false);

  const options = useMemo(
    () =>
      collections.map((c) => {
        const missing = missingLocales(c);
        return {
          id: c.id,
          label: previewFromLabel(c),
          missing,
          isComplete: missing.length === 0,
          // Términos por los que cmdk hace match además del label.
          keywords: [c.label.es, c.label.ca, c.label.en, c.slug].filter(
            (t): t is string => typeof t === 'string' && t.length > 0,
          ),
        };
      }),
    [collections],
  );

  const selected = options.find((o) => o.id === value);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          role="combobox"
          aria-expanded={open}
          aria-label="Colección"
          className={cn(
            'flex h-8 min-w-[200px] flex-1 items-center justify-between gap-2 rounded-md border border-input bg-background px-2 py-1 text-sm shadow-sm',
            'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
            !selected && 'text-muted-foreground',
          )}
        >
          <span className="truncate">
            {selected ? selected.label : 'Seleccionar colección'}
          </span>
          <ChevronsUpDown className="size-4 shrink-0 opacity-50" aria-hidden />
        </button>
      </PopoverTrigger>

      <PopoverContent className="w-[320px] p-0">
        <Command>
          <CommandInput placeholder="Buscar colección…" />
          <CommandList>
            <CommandEmpty>No hay colecciones que coincidan.</CommandEmpty>
            <CommandGroup>
              {options.map((o) => (
                <CommandItem
                  key={o.id}
                  value={o.id}
                  keywords={o.keywords}
                  disabled={!o.isComplete}
                  onSelect={() => {
                    onChange(o.id);
                    setOpen(false);
                  }}
                >
                  <Check
                    className={cn('size-4', o.id === value ? 'opacity-100' : 'opacity-0')}
                    aria-hidden
                  />
                  <span className="truncate">{o.label}</span>
                  {!o.isComplete && (
                    <span className="ml-auto shrink-0 text-xs text-muted-foreground">
                      faltan: {o.missing.join(', ')}
                    </span>
                  )}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
